import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { LineChart, Line, Area, AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { mlApi, ForecastData } from '../../services/api/ml.api';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';

const availableMetrics = [
  { name: 'cpu_usage', label: 'CPU Usage', unit: '%' },
  { name: 'memory_usage', label: 'Memory Usage', unit: '%' },
  { name: 'request_count', label: 'Request Count', unit: 'req/s' },
  { name: 'error_rate', label: 'Error Rate', unit: '%' },
  { name: 'response_time', label: 'Response Time', unit: 'ms' },
  { name: 'disk_usage', label: 'Disk Usage', unit: '%' },
];

export default function ForecastsPage() {
  const { t } = useTranslation(['ml', 'common']);
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState(availableMetrics[0]);
  const [horizon, setHorizon] = useState<24 | 48 | 168>(48); // Forecast horizon in hours
  const [forecastData, setForecastData] = useState<ForecastData[]>([]);
  const [showInsights, setShowInsights] = useState(true);

  useEffect(() => {
    if (selectedMetric) {
      fetchForecast();
    }
  }, [selectedMetric, horizon, selectedAppId]);

  const fetchForecast = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate time range: show last 24h for context, then forecast into the future
      const now = Date.now();
      const startTime = now - 24 * 60 * 60 * 1000; // 24 hours ago for historical context
      const endTime = now + horizon * 60 * 60 * 1000; // Forecast horizon hours into future

      const data = await mlApi.getForecasts(
        selectedMetric.name,
        startTime,
        endTime,
        selectedAppId
      );
      setForecastData(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load forecast data');
      console.error('Forecast fetch error:', err);
      setForecastData([]);
    } finally {
      setLoading(false);
    }
  };

  const getTrendFromData = () => {
    if (forecastData.length < 2) return 'stable';
    const predictions = forecastData.filter(d => d.isPrediction && d.predicted !== null);
    if (predictions.length < 2) return 'stable';

    const firstPrediction = predictions[0].predicted || 0;
    const lastPrediction = predictions[predictions.length - 1].predicted || 0;
    const change = ((lastPrediction - firstPrediction) / firstPrediction) * 100;

    if (change > 10) return 'increasing';
    if (change < -10) return 'decreasing';
    return 'stable';
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return (
          <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'decreasing':
        return (
          <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
          </svg>
        );
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return 'text-red-600 dark:text-red-400';
      case 'decreasing':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getCurrentValue = () => {
    const actualData = forecastData.filter(d => !d.isPrediction && d.actual !== null);
    if (actualData.length === 0) return 0;
    return actualData[actualData.length - 1].actual || 0;
  };

  const getPredictedPeak = () => {
    const predictions = forecastData.filter(d => d.isPrediction && d.predicted !== null);
    if (predictions.length === 0) return 0;
    return Math.max(...predictions.map(d => d.predicted || 0));
  };

  // Advanced Insights Generation
  const getForecastInsights = () => {
    const insights = [];
    const predictions = forecastData.filter(d => d.isPrediction && d.predicted !== null);
    if (predictions.length === 0) return insights;

    const current = getCurrentValue();
    const peak = getPredictedPeak();
    const trendType = getTrendFromData();
    const changePercent = ((peak - current) / current) * 100;

    // Capacity Alert
    if (selectedMetric.unit === '%' && peak > 80) {
      insights.push({
        type: 'critical',
        icon: '🚨',
        title: t('ml:forecasts.insights.capacity.warning.title'),
        description: t('ml:forecasts.insights.capacity.warning.description', {
          metric: selectedMetric.label,
          peak: peak.toFixed(1),
          horizon
        }),
        priority: peak > 90 ? 'critical' : 'high',
      });
    } else if (selectedMetric.unit === '%' && peak > 70) {
      insights.push({
        type: 'warning',
        icon: '⚠️',
        title: t('ml:forecasts.insights.capacity.approaching.title'),
        description: t('ml:forecasts.insights.capacity.approaching.description', {
          metric: selectedMetric.label,
          peak: peak.toFixed(1)
        }),
        priority: 'medium',
      });
    }

    // Trend Analysis
    if (trendType === 'increasing' && Math.abs(changePercent) > 20) {
      insights.push({
        type: 'trend',
        icon: '📈',
        title: t('ml:forecasts.insights.trend.growth.title'),
        description: t('ml:forecasts.insights.trend.growth.description', {
          metric: selectedMetric.label,
          percent: changePercent.toFixed(1)
        }),
        priority: changePercent > 50 ? 'high' : 'medium',
      });
    } else if (trendType === 'decreasing' && Math.abs(changePercent) > 20) {
      insights.push({
        type: 'opportunity',
        icon: '💰',
        title: t('ml:forecasts.insights.trend.decline.title'),
        description: t('ml:forecasts.insights.trend.decline.description', {
          metric: selectedMetric.label,
          percent: Math.abs(changePercent).toFixed(1)
        }),
        priority: 'info',
      });
    }

    // Seasonality Detection
    const hasVariability = predictions.some((p, i) => {
      if (i === 0) return false;
      const diff = Math.abs((p.predicted || 0) - (predictions[i - 1].predicted || 0));
      return diff > current * 0.15;
    });

    if (hasVariability) {
      insights.push({
        type: 'pattern',
        icon: '🔄',
        title: t('ml:forecasts.insights.variability.title'),
        description: t('ml:forecasts.insights.variability.description'),
        priority: 'info',
      });
    }

    // Stability Analysis
    if (trendType === 'stable' && peak < 60 && selectedMetric.unit === '%') {
      insights.push({
        type: 'stability',
        icon: '✅',
        title: t('ml:forecasts.insights.stable.title'),
        description: t('ml:forecasts.insights.stable.description', {
          metric: selectedMetric.label
        }),
        priority: 'info',
      });
    }

    return insights;
  };

  // Generate Actionable Recommendations
  const getRecommendations = () => {
    const recommendations = [];
    const peak = getPredictedPeak();
    const current = getCurrentValue();
    const trendType = getTrendFromData();

    // Scaling Recommendations
    if (selectedMetric.unit === '%' && peak > 80) {
      recommendations.push({
        icon: '🚀',
        title: t('ml:forecasts.recommendations.scaleNow.title'),
        description: t('ml:forecasts.recommendations.scaleNow.description'),
        action: t('ml:forecasts.recommendations.scaleNow.action'),
        urgency: 'critical',
        onClick: () => navigate('/infrastructure'),
      });
    } else if (selectedMetric.unit === '%' && peak > 70) {
      recommendations.push({
        icon: '📊',
        title: t('ml:forecasts.recommendations.planScaling.title'),
        description: t('ml:forecasts.recommendations.planScaling.description'),
        action: t('ml:forecasts.recommendations.planScaling.action'),
        urgency: 'high',
        onClick: () => navigate('/infrastructure'),
      });
    }

    // Alert Setup
    recommendations.push({
      icon: '🔔',
      title: t('ml:forecasts.recommendations.setAlerts.title'),
      description: t('ml:forecasts.recommendations.setAlerts.description', {
        threshold: Math.max(60, peak - 10).toFixed(0)
      }),
      action: t('ml:forecasts.recommendations.setAlerts.action'),
      urgency: 'medium',
      onClick: () => navigate('/alerts'),
    });

    // Cost Optimization
    if (trendType === 'decreasing' && peak < 40 && selectedMetric.unit === '%') {
      recommendations.push({
        icon: '💰',
        title: t('ml:forecasts.recommendations.optimizeCosts.title'),
        description: t('ml:forecasts.recommendations.optimizeCosts.description'),
        action: t('ml:forecasts.recommendations.optimizeCosts.action'),
        urgency: 'low',
        onClick: () => navigate('/infrastructure'),
      });
    }

    // Capacity Planning
    if (horizon === 168) {
      recommendations.push({
        icon: '📅',
        title: t('ml:forecasts.recommendations.capacityPlanning.title'),
        description: t('ml:forecasts.recommendations.capacityPlanning.description'),
        action: t('ml:forecasts.recommendations.capacityPlanning.action'),
        urgency: 'low',
        onClick: () => navigate('/metrics'),
      });
    }

    return recommendations.slice(0, 4);
  };

  // Get Risk Level
  const getRiskLevel = () => {
    const peak = getPredictedPeak();
    if (selectedMetric.unit === '%') {
      if (peak > 90) return { level: 'critical', color: 'red', label: t('ml:forecasts.riskLevel.critical') };
      if (peak > 80) return { level: 'high', color: 'orange', label: t('ml:forecasts.riskLevel.high') };
      if (peak > 70) return { level: 'medium', color: 'yellow', label: t('ml:forecasts.riskLevel.medium') };
      return { level: 'low', color: 'green', label: t('ml:forecasts.riskLevel.low') };
    }
    return { level: 'low', color: 'green', label: t('ml:forecasts.riskLevel.low') };
  };

  // Predict Time to Threshold
  const getTimeToThreshold = (threshold: number) => {
    const predictions = forecastData.filter(d => d.isPrediction && d.predicted !== null);
    if (predictions.length === 0) return null;

    const thresholdPoint = predictions.find(p => (p.predicted || 0) >= threshold);
    if (!thresholdPoint) return null;

    // Calculate actual time difference from now to threshold point
    const now = Date.now();
    const thresholdTime = new Date(thresholdPoint.time).getTime();
    const hoursToThreshold = Math.max(0, (thresholdTime - now) / (60 * 60 * 1000));

    return {
      hours: Math.round(hoursToThreshold),
      timestamp: thresholdPoint.time,
      value: thresholdPoint.predicted,
    };
  };

  const trend = getTrendFromData();
  const currentValue = getCurrentValue();
  const predictedPeak = getPredictedPeak();
  const riskLevel = getRiskLevel();
  const insights = getForecastInsights();
  const recommendations = getRecommendations();
  const timeToCapacity = selectedMetric.unit === '%' ? getTimeToThreshold(80) : null;

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('ml:forecasts.title')}
          <span className="ml-3 inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-3 py-1 text-sm font-semibold text-white">
            {t('ml:aiPowered')}
          </span>
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('ml:forecasts.subtitle')}</p>
      </div>

      {/* Forecast Horizon Selector */}
      <div className="mb-8 flex items-center space-x-4">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('ml:forecasts.horizon')}:</span>
        <div className="flex space-x-2">
          {[
            { value: 24, label: t('ml:forecasts.hours24') },
            { value: 48, label: t('ml:forecasts.hours48') },
            { value: 168, label: t('ml:forecasts.week') }
          ].map((h) => (
            <button
              key={h.value}
              onClick={() => setHorizon(h.value as 24 | 48 | 168)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                horizon === h.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {h.label}
            </button>
          ))}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {availableMetrics.map((metric) => {
          const isSelected = selectedMetric.name === metric.name;
          return (
            <div
              key={metric.name}
              onClick={() => setSelectedMetric(metric)}
              className={`cursor-pointer rounded-xl border p-6 shadow-sm transition-all hover:shadow-md ${
                isSelected
                  ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-900/10'
                  : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900'
              }`}
            >
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{metric.label}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{metric.unit}</p>
                </div>
                {isSelected && getTrendIcon(trend)}
              </div>
              {isSelected && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('ml:forecasts.current')}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {currentValue.toFixed(1)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('ml:forecasts.predictedPeak')}</p>
                    <p className={`text-2xl font-bold ${getTrendColor(trend)}`}>
                      {predictedPeak.toFixed(1)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Forecast Chart */}
      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-gray-600 dark:text-gray-400">{t('ml:forecasts.loading')}</p>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 shadow-sm dark:border-red-800 dark:bg-red-900/20">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      ) : forecastData.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-gray-600 dark:text-gray-400">
            {t('ml:forecasts.noData')}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {selectedMetric.label}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('ml:forecasts.actualVsPredicted')} ({horizon}h {t('ml:forecasts.horizon').toLowerCase()})
              </p>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={forecastData}>
              <defs>
                <linearGradient id="colorConfidence" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366F1" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#6366F1" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis dataKey="time" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                }}
              />
              <Legend />

              {/* Confidence interval */}
              <Area
                type="monotone"
                dataKey="upperBound"
                stroke="none"
                fill="url(#colorConfidence)"
                name="Upper Bound"
              />
              <Area
                type="monotone"
                dataKey="lowerBound"
                stroke="none"
                fill="url(#colorConfidence)"
                name="Lower Bound"
              />

              {/* Actual data */}
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#10B981"
                strokeWidth={2}
                dot={false}
                name="Actual"
              />

              {/* Predicted data */}
              <Line
                type="monotone"
                dataKey="predicted"
                stroke="#6366F1"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Predicted"
              />
            </AreaChart>
          </ResponsiveContainer>

          {/* Risk Level Banner */}
          {forecastData.length > 0 && (
            <div className={`mt-6 p-4 rounded-lg border-2 ${
              riskLevel.color === 'red' ? 'bg-red-50 border-red-500 dark:bg-red-900/10 dark:border-red-500' :
              riskLevel.color === 'orange' ? 'bg-orange-50 border-orange-500 dark:bg-orange-900/10 dark:border-orange-500' :
              riskLevel.color === 'yellow' ? 'bg-yellow-50 border-yellow-500 dark:bg-yellow-900/10 dark:border-yellow-500' :
              'bg-green-50 border-green-500 dark:bg-green-900/10 dark:border-green-500'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">
                    {riskLevel.color === 'red' ? '🔴' :
                     riskLevel.color === 'orange' ? '🟠' :
                     riskLevel.color === 'yellow' ? '🟡' : '🟢'}
                  </span>
                  <div>
                    <h3 className={`font-bold ${
                      riskLevel.color === 'red' ? 'text-red-900 dark:text-red-400' :
                      riskLevel.color === 'orange' ? 'text-orange-900 dark:text-orange-400' :
                      riskLevel.color === 'yellow' ? 'text-yellow-900 dark:text-yellow-400' :
                      'text-green-900 dark:text-green-400'
                    }`}>
                      {riskLevel.label}
                    </h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {timeToCapacity ?
                        t('ml:forecasts.riskLevel.capacityIn', { hours: timeToCapacity.hours }) :
                        riskLevel.level === 'low' ?
                          t('ml:forecasts.riskLevel.withinLimits') :
                          t('ml:forecasts.riskLevel.monitorClosely')
                      }
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-600 dark:text-gray-400">{t('ml:forecasts.predictedPeak')}</p>
                  <p className={`text-2xl font-bold ${
                    riskLevel.color === 'red' ? 'text-red-900 dark:text-red-400' :
                    riskLevel.color === 'orange' ? 'text-orange-900 dark:text-orange-400' :
                    riskLevel.color === 'yellow' ? 'text-yellow-900 dark:text-yellow-400' :
                    'text-green-900 dark:text-green-400'
                  }`}>
                    {predictedPeak.toFixed(1)}{selectedMetric.unit}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Insights and Recommendations Section */}
      {forecastData.length > 0 && showInsights && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AI Insights */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🤖</span>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('ml:forecasts.insights.title')}</h3>
                <span className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                  {t('ml:aiPowered')}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              {insights.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No significant insights detected for current forecast</p>
                </div>
              ) : (
                insights.map((insight, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${
                    insight.priority === 'critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800' :
                    insight.priority === 'high' ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800' :
                    insight.priority === 'medium' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-800' :
                    'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl">{insight.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{insight.title}</h4>
                        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{insight.description}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Actionable Recommendations */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-2xl">💡</span>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('ml:forecasts.recommendations.title')}</h3>
            </div>
            <div className="space-y-3">
              {recommendations.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No immediate actions required</p>
                </div>
              ) : (
                recommendations.map((rec, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border transition-all hover:shadow-md ${
                    rec.urgency === 'critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800' :
                    rec.urgency === 'high' ? 'bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:border-orange-800' :
                    rec.urgency === 'medium' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-800' :
                    'bg-gray-50 border-gray-200 dark:bg-gray-800/50 dark:border-gray-700'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <span className="text-2xl">{rec.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{rec.title}</h4>
                          {rec.urgency === 'critical' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                              Urgent
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{rec.description}</p>
                        <button
                          onClick={rec.onClick}
                          className={`mt-3 inline-flex items-center text-xs font-medium ${
                            rec.urgency === 'critical' ? 'text-red-600 dark:text-red-400' :
                            rec.urgency === 'high' ? 'text-orange-600 dark:text-orange-400' :
                            'text-indigo-600 dark:text-indigo-400'
                          } hover:underline`}
                        >
                          {rec.action}
                          <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Forecast Accuracy Info */}
      {forecastData.length > 0 && (
        <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/10">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-400">About ML Forecasts</p>
              <p className="mt-1 text-xs text-blue-800 dark:text-blue-300">
                Forecasts are generated using advanced machine learning models trained on historical data.
                Confidence intervals show the predicted range of values. Longer horizons have wider confidence bands.
                Use these predictions for capacity planning and proactive resource management.
              </p>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
